import { Pipe, PipeTransform } from '@angular/core';
import { PlanktonEvent, PlanktonOrigin } from '@models';

function niceCoord(
  c: number,
  negativeSuffix: string,
  positiveSuffix: string,
): string {
  const abs = Math.abs(c).toFixed(1);
  const suffix = c < 0 ? negativeSuffix : positiveSuffix;
  return abs + suffix;
}

const sep = ' / ';

// shows a title of a plankton event
@Pipe({
  name: 'eventTitle',
})
export class PlanktonEventTitlePipe implements PipeTransform {
  transform(event: PlanktonEvent): string {
    const s = event.measurementsOrFacts?.expedition?.value + sep;
    if (event.content.fieldNumber) {
      return s + event.content.fieldNumber;
    }
    // In Nansen Legacy cruises fieldNumber was discouraged. Instead eventID
    // of type UUIDv1 was used.These ids often differ only in the first 8 chars.
    // For better readability only these are presented here
    return s + event.id.substring(0, 8);
  }
}

// shows a short abstract of important plankton event meta data
@Pipe({
  name: 'eventAbstract',
})
export class PlanktonEventAbstractPipe implements PipeTransform {
  transform(event: PlanktonEvent): string {
    const parts = [
      event.content.eventDate.substring(0, 10),
      niceCoord(event.content.decimalLatitude, '°S', '°N'),
      niceCoord(event.content.decimalLongitude, '°W', '°E'),
    ];
    if (event.measurementsOrFacts?.gearType) {
      parts.push(event.measurementsOrFacts.gearType!.value);
    }
    return parts.join(sep);
  }
}

// shows a title of a plankton origin (or import batch)
@Pipe({
  name: 'originTitle',
})
export class PlanktonOriginTitlePipe implements PipeTransform {
  transform(origin: PlanktonOrigin): string {
    switch (origin.sourceType) {
      case 'file':
        return origin.metaInfo['sourceFileName'] as string;
      case 'google':
        return (
          (origin.metaInfo['googleDocumentTitle'] as string) +
          ' / ' +
          (origin.metaInfo['googleTabName'] as string)
        );
      case 'script':
        return (
          (origin.metaInfo['sourceScript'] as string) +
          ' / ' +
          (origin.metaInfo['sourceFileName'] as string)
        );
      default:
        return 'N/A';
    }
  }
}

// shows a short abstract of a plankton origin (or import batch)
@Pipe({
  name: 'originAbstract',
})
export class PlanktonOriginAbstractPipe implements PipeTransform {
  transform(origin: PlanktonOrigin): string {
    const parts = [];
    parts.push(origin.numberOfItems.toString());
    if (!origin.isPublished) {
      parts.push('unpublished');
    }
    parts.push(origin.planktonType);
    let cl = origin.darwinCoreClass;
    if (origin.numberOfItems != 1) {
      cl += 's';
    }
    parts.push(cl);
    return parts.join(' ');
  }
}

// shows the icon identifier for a plankton origin based on the sourceType
@Pipe({
  name: 'originIcon',
})
export class PlanktonOriginIconPipe implements PipeTransform {
  transform(origin: PlanktonOrigin): string {
    if (!origin.isPublished) {
      return 'pending';
    }
    switch (origin.sourceType) {
      case 'google':
        return 'cloud_download';
      case 'file':
        return 'description';
      case 'script':
        return 'terminal';
      default:
        return 'grid_on';
    }
  }
}

// shows a link to WoRMS
@Pipe({
  name: 'wormsURL',
})
export class WoRMSURLPipe implements PipeTransform {
  transform(scientificNameID: string): string {
    const id = scientificNameID.split(':').at(-1);
    return 'https://www.marinespecies.org/aphia.php?p=taxdetails&id=' + id;
  }
}
